// ===================================================================
// 🚀 APP PRINCIPAL — Ingressa
// ===================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NotificationProvider } from './contexts/NotificationContext';
import { AdminProcessProvider } from './contexts/AdminProcessContext';
import { Header } from './components/layout/Header';
import { Navigation } from './components/layout/Navigation';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { Modal } from './components/ui/Modal';
import { LoginScreen } from './pages/auth/LoginScreen';
import { CandidateRegisterScreen } from './pages/auth/CandidateRegisterScreen';
import { ResetPasswordScreen } from './pages/auth/ResetPasswordScreen';
import { ChangePasswordScreen } from './pages/auth/ChangePasswordScreen';
import { ProcessManager } from './pages/admin/ProcessManager';
import { CourseManager } from './pages/admin/CourseManager';
import { CourseTypesManager } from './pages/admin/CourseTypesManager';
import { PaymentManager } from './pages/admin/PaymentManager';
import { QuotaManager } from './pages/admin/QuotaManager';
import { PaymentMethodManager } from './pages/admin/PaymentMethodManager';
import { RejectionReasonsManager } from './pages/admin/RejectionReasonsManager';
import { EssayEliminationReasonsManager } from './pages/admin/EssayEliminationReasonsManager';
import { UserManager } from './pages/admin/UserManager';
import AnalysisManager from './pages/admin/AnalysisManager';
import { AppealManager } from './pages/admin/AppealManager';
import { EditalChallengeManager } from './pages/admin/EditalChallengeManager';
import { ConfigManager } from './pages/admin/ConfigManager';
import { TiebreakerManager } from './pages/admin/TiebreakerManager';
import { QuestionnaireManager } from './pages/admin/QuestionnaireManager';
import { ReportsManager } from './pages/admin/ReportsManager';
import { InstitutionManager } from './pages/master/InstitutionManager';
import { MasterProfileModal } from './components/master/MasterProfileModal';
import { AnalystChangePasswordModal } from './components/analyst/AnalystChangePasswordModal';
import { PainelControle } from './pages/shared/PainelControle';
import { AnalystArea } from './pages/analyst/AnalystArea';
import { SpecialAttendanceAnalysis } from './pages/analyst/SpecialAttendanceAnalysis';
import { SpecialAttendanceTypesManager } from './pages/admin/SpecialAttendanceTypesManager';
import { CandidateArea } from './pages/candidate/CandidateArea';
import { styles } from './styles/globalStyles';
import { apiClient } from './api/apiClient';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Phase0NavGuard } from './components/layout/Phase0NavGuard';
import { ExamEnrolledList } from './pages/admin/ExamEnrolledList';
import { ExamPhasePlaceholderPage } from './pages/shared/ExamPhasePlaceholderPage';
import { ExamObjectiveAdminPage } from './pages/admin/ExamObjectiveAdminPage';
import { ExamEssayAdminPage } from './pages/admin/ExamEssayAdminPage';
import { ExamEssayAnalysis } from './pages/analyst/ExamEssayAnalysis';
import { ExamObjectiveAnalysis } from './pages/analyst/ExamObjectiveAnalysis';
import { AboutPage } from './pages/shared/AboutPage';
import {
  blocksStaffInCandidatePwa,
  CANDIDATE_PWA_STAFF_BLOCK_MESSAGE,
  isCandidatePwaContext,
} from './pwa/candidatePwa';
import { useCandidateMobileLayout } from './hooks/useCandidateMobileLayout';

function App() {
  const SESSION_IDLE_DEFAULT_MINUTES = 15;
  const SESSION_WARNING_SECONDS = 60;
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mountKey, setMountKey] = useState(0); // força remontagem ao entrar na área do candidato
  const [view, setView] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const v = params.get('view');
    if (token && v === 'reset-password') return 'reset-password';
    if (token && v === 'complete-registration') return 'complete-registration';
    return 'login';
  });
  const [resetToken, setResetToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || null;
  });
  const [currentPage, setCurrentPage] = useState(null);
  const [registerType, setRegisterType] = useState('general');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(SESSION_IDLE_DEFAULT_MINUTES);
  const [idleWarningOpen, setIdleWarningOpen] = useState(false);
  const [idleRemainingSeconds, setIdleRemainingSeconds] = useState(SESSION_WARNING_SECONDS);
  const [loginAlert, setLoginAlert] = useState('');
  const lastActivityRef = useRef(Date.now());
  const logoutInProgressRef = useRef(false);
  const idleWarningOpenRef = useRef(false);
  const isCandidateMobile = useCandidateMobileLayout();
  useEffect(() => {
    idleWarningOpenRef.current = idleWarningOpen;
  }, [idleWarningOpen]);

  useEffect(() => {
    const active = userRole === 'candidate' && isCandidateMobile;
    document.body.classList.toggle('ingressa-candidate-mobile', active);
    document.body.classList.toggle('ingressa-candidate-pwa', active && isCandidatePwaContext());
    return () => {
      document.body.classList.remove('ingressa-candidate-mobile', 'ingressa-candidate-pwa');
    };
  }, [userRole, isCandidateMobile]);


  useEffect(() => {
    const checkUser = async () => {
      try {
        // Sessão verificada via apiClient.auth.getSession()
        const { data, error } = await apiClient.auth.getSession();
        
        if (data?.session?.user) {
          const userData = data.session.user;
          const role = userData.role || 'candidate';
          if (blocksStaffInCandidatePwa(role)) {
            await apiClient.auth.signOut();
            localStorage.removeItem('current_user');
            setLoginAlert(CANDIDATE_PWA_STAFF_BLOCK_MESSAGE);
            setView('login');
            console.log('🔐 Sessão de equipe encerrada no app candidato (PWA)');
          } else {
            console.log('🔐 Sessão recuperada:', userData.email);
            setUser(userData);
            setLoginAlert('');
            setShowProfileModal(false);
            setMountKey(k => k + 1); // força dados frescos ao entrar
            setUserRole(role);

            // Definir página inicial baseada no role
            if (role === 'master') {
              setCurrentPage('institutions');
            } else if (role === 'analyst' || role === 'admin' || role === 'campus') {
              setCurrentPage('painel');
            } else {
              setCurrentPage('dashboard');
            }
          }
        } else {
          console.log('❌ Nenhuma sessão ativa encontrada');
        }
      } catch (error) {
        if (error?.reason === 'inactivity_timeout') {
          setLoginAlert('Sessão encerrada por inatividade. Faça login novamente.');
        }
        console.error('Erro ao verificar sessão:', error);
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, []);

  useEffect(() => {
    const handleNavigate = (ev) => {
      const page = ev?.detail?.page;
      if (typeof page === 'string' && page) {
        setCurrentPage(page);
      }
    };
    window.addEventListener('ingressa:navigate', handleNavigate);
    return () => window.removeEventListener('ingressa:navigate', handleNavigate);
  }, []);

  const handleLoginSuccess = useCallback(async (userData) => {
    if (blocksStaffInCandidatePwa(userData.role)) {
      await apiClient.auth.signOut();
      localStorage.removeItem('current_user');
      setLoginAlert(CANDIDATE_PWA_STAFF_BLOCK_MESSAGE);
      setView('login');
      return;
    }
    setUser(userData);
    setLoginAlert('');
    setShowProfileModal(false);
    setMountKey(k => k + 1); // força dados frescos ao fazer login
    setUserRole(userData.role || 'candidate');
    setCurrentPage(userData.role === 'master' ? 'institutions' : ['analyst', 'admin', 'campus'].includes(userData.role) ? 'painel' : userData.role === 'candidate' ? 'dashboard' : 'processes');

    localStorage.setItem('current_user', JSON.stringify(userData));
    console.log('🔐 Usuário salvo no localStorage de compatibilidade');
  }, []);

  const handleRegisterSuccess = useCallback((userData) => {
    setUser(userData);
    setShowProfileModal(false);
    setUserRole(userData.role || 'candidate');
    setCurrentPage('dashboard');
  }, []);

  const handleRegistrationComplete = useCallback(() => {
    window.history.replaceState({}, '', window.location.pathname);
    setResetToken(null);
    setLoginAlert('Cadastro concluído! Faça login com seu e-mail e senha.');
    setView('login');
  }, []);

  const handleLogout = useCallback(async () => {
    await apiClient.auth.signOut();
    localStorage.removeItem('current_user');
    setUser(null);
    setUserRole(null);
    setShowProfileModal(false);
    setView('login');
    setCurrentPage('processes');
    setIdleWarningOpen(false);
    setIdleRemainingSeconds(SESSION_WARNING_SECONDS);
    logoutInProgressRef.current = false;
    console.log('🔓 Logout completo - sessões removidas');
  }, []);

  const handleInactivityLogout = useCallback(async () => {
    if (logoutInProgressRef.current) return;
    logoutInProgressRef.current = true;
    setIdleWarningOpen(false);
    setLoginAlert('Sessão encerrada por inatividade. Faça login novamente.');
    await handleLogout();
  }, [handleLogout]);

  useEffect(() => {
    let cancelled = false;
    if (!user || userRole !== 'candidate') {
      setIdleWarningOpen(false);
      setIdleRemainingSeconds(SESSION_WARNING_SECONDS);
      setIdleTimeoutMinutes(SESSION_IDLE_DEFAULT_MINUTES);
      return undefined;
    }
    const loadIdleConfig = async () => {
      try {
        const cfg = await apiClient.getConfig();
        const minutes = Number(cfg?.session_idle_timeout_minutes);
        if (!cancelled) {
          setIdleTimeoutMinutes(Number.isInteger(minutes) ? minutes : SESSION_IDLE_DEFAULT_MINUTES);
        }
      } catch {
        if (!cancelled) setIdleTimeoutMinutes(SESSION_IDLE_DEFAULT_MINUTES);
      }
    };
    loadIdleConfig();
    return () => { cancelled = true; };
  }, [user, userRole]);

  useEffect(() => {
    if (!user || userRole !== 'candidate') return undefined;
    lastActivityRef.current = Date.now();
    const markActivity = () => {
      // Quando o aviso está aberto, não aceitar atividade passiva (ex.: mover mouse)
      // para evitar fechar o modal sem confirmação explícita.
      if (idleWarningOpenRef.current) return;
      lastActivityRef.current = Date.now();
    };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((evt) => window.addEventListener(evt, markActivity, { passive: true }));

    const timerId = window.setInterval(() => {
      const timeoutSeconds = Math.max(SESSION_WARNING_SECONDS, idleTimeoutMinutes * 60);
      const elapsedSeconds = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remainingSeconds = timeoutSeconds - elapsedSeconds;
      if (remainingSeconds <= 0) {
        handleInactivityLogout();
        return;
      }
      if (remainingSeconds <= SESSION_WARNING_SECONDS) {
        setIdleRemainingSeconds(remainingSeconds);
        setIdleWarningOpen(true);
      }
    }, 1000);

    return () => {
      window.clearInterval(timerId);
      events.forEach((evt) => window.removeEventListener(evt, markActivity));
    };
  }, [user, userRole, idleTimeoutMinutes, handleInactivityLogout]);

  const handleKeepSessionAlive = useCallback(async () => {
    const { data, error } = await apiClient.auth.getSession();
    if (data?.session?.user) {
      lastActivityRef.current = Date.now();
      setIdleWarningOpen(false);
      return;
    }
    if (error?.reason === 'inactivity_timeout' || error?.status === 401) {
      await handleInactivityLogout();
    }
  }, [handleInactivityLogout]);

  const renderCurrentPage = useCallback(() => {
    if (userRole === 'master') {
      if (currentPage === 'about') return <AboutPage userRole={userRole} />;
      if (currentPage === 'config') {
        return <ConfigManager isMaster onSaved={() => setCurrentPage('institutions')} />;
      }
      return <InstitutionManager />;
    }
    if (userRole === 'candidate') {
      if (currentPage === 'about') return <AboutPage userRole={userRole} />;
      return (
        <CandidateArea
          key={`${user?.id}-${mountKey}`}
          user={user}
          onUserUpdate={setUser}
          showProfileModal={showProfileModal}
          onCloseProfileModal={() => setShowProfileModal(false)}
        />
      );
    }

    switch (currentPage) {
      case 'about':
        return <AboutPage userRole={userRole} />;
      case 'processes':
        return <ProcessManager user={user} />;
      case 'painel':
        return <PainelControle user={user} />;
      case 'courses':
        return <CourseManager user={user} />;
      case 'course-types':
        return userRole === 'admin' ? <CourseTypesManager /> : <ProcessManager user={user} />;
      case 'quotas':
        return userRole === 'admin' ? <QuotaManager user={user} /> : <ProcessManager user={user} />;
      case 'payment_methods':
        return userRole === 'admin' ? <PaymentMethodManager user={user} /> : <ProcessManager user={user} />;
      case 'analyst-applications':
        return <AnalystArea user={user} activeTab="applications" />;
      case 'analyst-exemptions':
        return <AnalystArea user={user} activeTab="exemptions" />;
      case 'analyst-resources':
        return <AnalystArea user={user} activeTab="resources" />;
      case 'analyst-special-attendance':
        return <AnalystArea user={user} activeTab="special-attendance" />;
      case 'exam-objective':
        if (userRole === 'admin') return <ExamObjectiveAdminPage />;
        if (userRole === 'analyst') return <ExamObjectiveAnalysis />;
        return <ProcessManager user={user} />;
      case 'exam-essay':
        if (userRole === 'admin') return <ExamEssayAdminPage />;
        if (userRole === 'analyst') return <ExamEssayAnalysis />;
        return <ProcessManager user={user} />;
      case 'admin-special-attendance':
        return userRole === 'admin' ? <SpecialAttendanceAnalysis user={user} /> : <ProcessManager user={user} />;
      case 'special-attendance-types':
        return userRole === 'admin' ? <SpecialAttendanceTypesManager /> : <ProcessManager user={user} />;
      case 'payments':
        return userRole === 'admin' ? <PaymentManager user={user} /> : <ProcessManager user={user} />;
      case 'reasons':
        return userRole === 'admin' ? <RejectionReasonsManager user={user} /> : <ProcessManager user={user} />;
      case 'essay-elimination-reasons':
        return userRole === 'admin' ? <EssayEliminationReasonsManager /> : <ProcessManager user={user} />;
      case 'tiebreakers':
        return userRole === 'admin' ? <TiebreakerManager user={user} /> : <ProcessManager user={user} />;
      case 'users':
        return userRole === 'admin' ? <UserManager user={user} /> : <ProcessManager user={user} />;
      case 'analysis':
        return userRole === 'admin' ? <AnalysisManager user={user} /> : <ProcessManager user={user} />;
      case 'exam-logistics':
        return userRole === 'admin' ? <ExamEnrolledList /> : <ProcessManager user={user} />;
      case 'appeals':
        return userRole === 'admin' ? <AppealManager user={user} /> : <ProcessManager user={user} />;
      case 'edital-challenges':
        return userRole === 'admin' ? <EditalChallengeManager /> : <ProcessManager user={user} />;
      case 'reports':
        return (userRole === 'admin' || userRole === 'campus') ? <ReportsManager userRole={userRole} /> : <ProcessManager user={user} />;
      case 'questionnaires':
        return userRole === 'admin' ? <QuestionnaireManager user={user} /> : <ProcessManager user={user} />;
      case 'config':
        return userRole === 'admin' ? (
          <ConfigManager onSaved={() => setCurrentPage('painel')} />
        ) : (
          <ProcessManager user={user} />
        );
      default:
        if (userRole === 'analyst') {
          return <AnalystArea user={user} activeTab="exemptions" />;
        }
        if (userRole === 'campus') {
          return <PainelControle user={user} />;
        }
        return <ProcessManager user={user} />;
    }
  }, [currentPage, userRole, user, showProfileModal, mountKey, setCurrentPage]);

  const isAuthDeepLink = view === 'complete-registration'
    || (view === 'reset-password' && !!resetToken);

  if (loading && !isAuthDeepLink) {
    return (
      <div style={styles.authContainer}>
        <LoadingSpinner text="Carregando..." />
      </div>
    );
  }

  if (view === 'register' || view === 'complete-registration') {
    return (
      <CandidateRegisterScreen
        setView={setView}
        registrationToken={view === 'complete-registration' ? resetToken : null}
        onRegistrationComplete={handleRegistrationComplete}
      />
    );
  }

  if (view === 'reset-password' && resetToken) {
    return (
      <ResetPasswordScreen
        token={resetToken}
        onSuccess={() => {
          window.history.replaceState({}, '', window.location.pathname);
          setResetToken(null);
          setView('login');
        }}
      />
    );
  }
    
  if (user && user.must_change_password && ['admin', 'analyst', 'master'].includes(user.role || '')) {
    return (
      <ChangePasswordScreen
        user={user}
        onSuccess={(updatedUser) => {
          setUser(updatedUser);
          setShowProfileModal(false);
          setMountKey(k => k + 1);
          setUserRole(updatedUser.role || 'candidate');
          setCurrentPage(updatedUser.role === 'master' ? 'institutions' : ['analyst', 'admin', 'campus'].includes(updatedUser.role) ? 'painel' : 'dashboard');
          localStorage.setItem('current_user', JSON.stringify(updatedUser));
        }}
        onUseAnotherAccount={async () => {
          await apiClient.auth.signOut();
          localStorage.removeItem('current_user');
          setUser(null);
          setUserRole(null);
          setCurrentPage('processes');
          setView('login');
        }}
      />
    );
  }

  if (user) {
    const mainContent = (
      <div style={{...styles.container, ...styles.mainContainer}}>
        <div className="no-print" style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          backgroundColor: 'white',
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)'
        }}>
          <Header
            user={user}
            onLogout={handleLogout}
            onOpenProfile={(userRole === 'candidate' || userRole === 'master' || userRole === 'analyst') ? () => setShowProfileModal(true) : undefined}
            profileButtonLabel={userRole === 'analyst' ? 'Alterar senha' : 'Meus dados'}
            onOpenAbout={() => setCurrentPage('about')}
            aboutActive={currentPage === 'about'}
          />
          <Navigation 
            currentPage={currentPage} 
            setCurrentPage={setCurrentPage} 
            userRole={userRole} 
          />
        </div>
        <main key={currentPage || 'default'} className="ingressa-content-container" style={{ flex: 1, minHeight: 0 }}>
          <ErrorBoundary>
            {renderCurrentPage()}
          </ErrorBoundary>
        </main>
      </div>
    );
    return (
      <NotificationProvider>
        {(userRole === 'admin' || userRole === 'analyst' || userRole === 'campus' || userRole === 'master') ? (
          <AdminProcessProvider userRole={userRole}>
            <Phase0NavGuard currentPage={currentPage} setCurrentPage={setCurrentPage} userRole={userRole} />
            {mainContent}
            {showProfileModal && userRole === 'master' && (
              <MasterProfileModal
                user={user}
                onClose={() => setShowProfileModal(false)}
                onUserUpdate={(u) => {
                  setUser(u);
                  try {
                    localStorage.setItem('current_user', JSON.stringify(u));
                  } catch (_) {}
                }}
              />
            )}
            {showProfileModal && userRole === 'analyst' && (
              <AnalystChangePasswordModal
                onClose={() => setShowProfileModal(false)}
                onUserUpdate={(u) => {
                  setUser(u);
                  try {
                    localStorage.setItem('current_user', JSON.stringify(u));
                  } catch (_) {}
                }}
              />
            )}
          </AdminProcessProvider>
        ) : (
          mainContent
        )}
        <Modal
          isOpen={idleWarningOpen && userRole === 'candidate'}
          onClose={() => {}}
          title="Sua sessão está para expirar"
        >
          <p style={{ marginBottom: '12px', color: '#374151' }}>
            Você será deslogado em <strong>{idleRemainingSeconds}</strong> segundos por inatividade.
          </p>
          <p style={{ marginBottom: '12px', color: '#6B7280', fontSize: '13px' }}>
            Para continuar conectado, clique no botão abaixo.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" style={styles.button} onClick={handleKeepSessionAlive}>
              Continuar conectado
            </button>
          </div>
        </Modal>
      </NotificationProvider>
    );
  }

  return (
    <LoginScreen 
      setView={setView} 
      onLoginSuccess={handleLoginSuccess}
      setRegisterType={setRegisterType}
      forcedMessage={loginAlert}
    />
  );
}

export default App; 